import React, { memo, useCallback, useMemo } from 'react'
import ControlledRadioGroupInput, {
  IControlledRadioGroupInputProps,
} from 'transfix-form/components/ControlledRadioGroupInput'
import { FormHelperText } from 'transfix-ui/components/FormHelperText'
import { FormModal, IFormModalProps } from 'transfix-ui/components/Modals/FormModal'
import Box from 'transfix-ui/core/Box'
import Grid from 'transfix-ui/core/Grid'
import Typography from 'transfix-ui/core/Typography'
import { TCarrierLoadPreferencesSubmitHandler } from '../../types'
import { IUseHeavyLoadsFormProps, useHeavyLoadsForm } from './hooks/useHeavyLoadsForm'
import { getErrorMessage, THeavyLoadsSchema } from './schema/HeavyLoadsSchema'

export interface IHeavyLoadsModalProps extends Pick<IFormModalProps, 'onClose'> {
  /**
   * @default {}
   */
  defaultValues?: IUseHeavyLoadsFormProps['defaultValues']
  loading: boolean
  onSubmit: TCarrierLoadPreferencesSubmitHandler<THeavyLoadsSchema>
}

const HEAVY_LOADS_OPTIONS: IControlledRadioGroupInputProps['options'] = [
  { label: 'Yes', id: true },
  { label: 'No', id: false },
]

const HeavyLoadsModal = memo<IHeavyLoadsModalProps>(
  ({ defaultValues = {}, loading, onClose, onSubmit }) => {
    const { control, errors, handleSubmit } = useHeavyLoadsForm({ defaultValues })
    const heavyLoadsPreferenceErrors = getErrorMessage(errors, 'isHeavyLoadCapable')

    const submitHandler = useCallback(
      async (formValues: THeavyLoadsSchema) => {
        const success = await onSubmit(formValues)
        if (success) {
          onClose()
        }
      },
      [onClose, onSubmit]
    )

    const actions: IFormModalProps['actions'] = useMemo(
      () => [
        {
          label: 'Cancel',
          type: 'dismiss',
          action: onClose,
          disabled: loading,
        },
        {
          label: 'Update',
          type: 'confirm',
          action: handleSubmit(submitHandler),
          disabled: loading,
        },
      ],
      [handleSubmit, loading, onClose, submitHandler]
    )

    return (
      <FormModal title='Heavy loads' actions={actions} size={5} onClose={onClose} open>
        <Grid container>
          <Grid item xs={12}>
            <Box mb={1.5}>
              <Typography>Are you willing to run heavy loads over 42k lbs?</Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <ControlledRadioGroupInput
              control={control}
              name='isHeavyLoadCapable'
              options={HEAVY_LOADS_OPTIONS}
              stacked={false}
            />
            {!!heavyLoadsPreferenceErrors && (
              <FormHelperText className='Mui-error'>{heavyLoadsPreferenceErrors}</FormHelperText>
            )}
          </Grid>
        </Grid>
      </FormModal>
    )
  }
)

export default HeavyLoadsModal
